/**
 * Pan, zoom and level-of-detail for the narrative graph.
 *
 * ## The zoom is the query
 *
 * This board holds a campaign's whole window — thousands of threads. The only honest way to
 * draw that is the way a map does it: show countries, then counties, then streets, and let the
 * zoom decide which. So `k` is not decoration here, it is what selects the level of the data
 * that gets drawn. Everything else in this file exists to make that gesture feel like a map
 * rather than like a slider.
 *
 * ## Why targets are separate from current values
 *
 * Every interaction writes `tk/tx/ty` and the frame loop eases `k/x/y` toward them. A wheel
 * event that wrote `k` directly would step, and a stepping zoom over a node graph reads as a
 * redraw rather than as movement — the eye loses which node it was following. Easing is what
 * keeps a node identifiable across a zoom, which is the whole reason the levels are nested.
 */

export interface Viewport {
  /** Current scale, eased toward `tk`. World units → device pixels. */
  k: number
  x: number
  y: number
  tk: number
  tx: number
  ty: number
  minK: number
  maxK: number
  /**
   * The scale at which the whole world fits the canvas.
   *
   * Everything the reader experiences as "zoom" is `k / fitK`, not `k`. The world has fixed
   * coordinates (see `RING_R`), so `k` alone would mean something different on a laptop and on
   * a briefing-room display — and the level-of-detail stops, which decide what is drawn, would
   * then depend on the size of the screen rather than on how far the reader has gone in.
   */
  fitK: number
}

export function makeViewport(minZoom: number, maxZoom: number): Viewport {
  return { k: 1, x: 0, y: 0, tk: 1, tx: 0, ty: 0, minK: minZoom, maxK: maxZoom, fitK: 1 }
}

/** Relative zoom — 1.0 means "the whole world fits". This is what the reader sees. */
export const zoomOf = (v: Viewport) => v.k / v.fitK

/**
 * Re-fit for a new canvas size, preserving where the reader is.
 *
 * The world point currently at the centre of the screen stays at the centre, and the relative
 * zoom is unchanged. That is what makes a resize — a window drag, the full-screen view opening
 * — feel like the window changed rather than the map.
 */
export function refit(v: Viewport, width: number, height: number, worldRadius: number) {
  const nextFit = (Math.min(width, height) * 0.40) / worldRadius
  const zoom = v.fitK === 0 ? 1 : zoomOf(v)
  // Where the old centre was pointing, in world units.
  const wx = v.k === 0 ? 0 : (width / 2 - v.tx) / v.k
  const wy = v.k === 0 ? 0 : (height / 2 - v.ty) / v.k
  v.fitK = nextFit
  v.tk = nextFit * zoom
  v.k = v.tk
  v.tx = width / 2 - wx * v.k
  v.ty = height / 2 - wy * v.k
  v.x = v.tx
  v.y = v.ty
}

/**
 * Advance one frame toward the target. Returns true while still moving, so the caller can
 * stop re-rendering a still scene.
 */
export function ease(v: Viewport): boolean {
  v.k += (v.tk - v.k) * 0.26
  v.x += (v.tx - v.x) * 0.26
  v.y += (v.ty - v.y) * 0.26
  if (Math.abs(v.tk - v.k) < 1e-4) v.k = v.tk
  if (Math.abs(v.tx - v.x) < 0.06) v.x = v.tx
  if (Math.abs(v.ty - v.y) < 0.06) v.y = v.ty
  return Math.abs(v.tk - v.k) > 1e-5
    || Math.abs(v.tx - v.x) > 0.05
    || Math.abs(v.ty - v.y) > 0.05
}

/** Zoom about a screen point, so the thing under the cursor stays under the cursor. */
export function zoomAt(v: Viewport, sx: number, sy: number, factor: number) {
  // The clamps are on RELATIVE zoom, so they mean the same thing at any canvas size.
  const nk = Math.max(v.minK * v.fitK, Math.min(v.maxK * v.fitK, v.tk * factor))
  const wx = (sx - v.tx) / v.tk
  const wy = (sy - v.ty) / v.tk
  v.tk = nk
  v.tx = sx - wx * nk
  v.ty = sy - wy * nk
}

/** Put a world point in the middle of the screen, optionally at a given scale. */
export function centreOn(
  v: Viewport, wx: number, wy: number, sw: number, sh: number, zoom?: number,
) {
  if (zoom !== undefined) {
    v.tk = Math.max(v.minK, Math.min(v.maxK, zoom)) * v.fitK
  }
  v.tx = sw / 2 - wx * v.tk
  v.ty = sh / 2 - wy * v.tk
}

/** Back to the whole world, centred. The world's origin is its centre. */
export function reset(v: Viewport, width: number, height: number) {
  v.tk = v.fitK
  v.tx = width / 2
  v.ty = height / 2
}

export const px = (v: Viewport, x: number) => x * v.k + v.x
export const py = (v: Viewport, y: number) => y * v.k + v.y

/**
 * Radius under zoom, deliberately sublinear.
 *
 * Positions scale with `k` and radii with `k^0.55`, so a crowded region genuinely separates
 * as you descend instead of scaling up as one solid mass. Linear radii would keep every
 * cluster looking exactly as dense at every zoom, which would make the zoom useless.
 */
export const scaleRadius = (v: Viewport, r: number) =>
  r * v.fitK * Math.pow(zoomOf(v), 0.55)

// ── level of detail ──────────────────────────────────────────────────────────

export type Level = 'SUBJECT' | 'NARRATIVE' | 'THREAD'

/** Where one level hands over to the next. */
export const LEVEL_STOPS = { narrative: 1.7, thread: 4.2 } as const

export function levelFor(zoom: number): Level {
  const k = zoom
  if (k >= LEVEL_STOPS.thread) return 'THREAD'
  if (k >= LEVEL_STOPS.narrative) return 'NARRATIVE'
  return 'SUBJECT'
}

/**
 * How present each level is at this scale, 0..1.
 *
 * Levels cross-fade over a band rather than switching at a threshold. A hard switch at a
 * single `k` makes the whole picture blink during a continuous gesture, and a reader who
 * blinked with it has to re-find where they were.
 */
export function levelAlpha(zoom: number): Record<Level, number> {
  const k = zoom
  const band = (v: number, from: number, to: number) =>
    Math.max(0, Math.min(1, (v - from) / (to - from)))
  const toNarrative = band(k, LEVEL_STOPS.narrative * 0.72, LEVEL_STOPS.narrative)
  const toThread = band(k, LEVEL_STOPS.thread * 0.78, LEVEL_STOPS.thread)
  return {
    SUBJECT: 1 - toNarrative,
    NARRATIVE: toNarrative * (1 - toThread),
    THREAD: toThread,
  }
}

// ── input ────────────────────────────────────────────────────────────────────

export interface NavHandle {
  /** True when the last pointer sequence was a drag, so a click handler can ignore it. */
  dragged: () => boolean
  detach: () => void
}

/**
 * Wheel-zoom, drag-pan and two-finger pinch on a canvas.
 *
 * Listeners for move/up go on the window rather than the canvas: a drag that leaves the
 * element mid-gesture would otherwise stick, and the map would keep panning after the button
 * came up somewhere else.
 */
export function attachNav(canvas: HTMLCanvasElement, v: Viewport): NavHandle {
  let drag: { x: number, y: number, vx: number, vy: number } | null = null
  let moved = 0

  const onWheel = (e: WheelEvent) => {
    e.preventDefault()
    const r = canvas.getBoundingClientRect()
    if (e.shiftKey) {
      v.tx -= e.deltaY
      return
    }
    // ctrlKey is what a trackpad pinch reports; it earns a steeper factor so the gesture
    // covers the same range of scale as it would on a map.
    zoomAt(v, e.clientX - r.left, e.clientY - r.top,
      Math.pow(1.0018, -e.deltaY * (e.ctrlKey ? 2.6 : 1)))
  }
  const onDown = (e: MouseEvent) => {
    if (e.button !== 0) return
    drag = { x: e.clientX, y: e.clientY, vx: v.tx, vy: v.ty }
    moved = 0
  }
  const onMove = (e: MouseEvent) => {
    if (!drag) return
    const dx = e.clientX - drag.x
    const dy = e.clientY - drag.y
    moved = Math.max(moved, Math.abs(dx) + Math.abs(dy))
    if (moved > 3) {
      v.tx = drag.vx + dx
      v.ty = drag.vy + dy
      // Pan is applied to BOTH target and current: easing a drag lags the cursor, and a map
      // whose surface trails the finger feels broken rather than smooth.
      v.x = v.tx
      v.y = v.ty
      canvas.style.cursor = 'grabbing'
    }
  }
  const onUp = () => {
    if (drag) {
      drag = null
      canvas.style.cursor = 'default'
    }
  }

  let touch: {
    d?: number, cx?: number, cy?: number
    single?: boolean, x?: number, y?: number, vx?: number, vy?: number
  } | null = null
  const onTouchStart = (e: TouchEvent) => {
    const r = canvas.getBoundingClientRect()
    const a = e.touches[0]
    const b = e.touches[1]
    if (e.touches.length === 2 && a && b) {
      touch = {
        d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        cx: (a.clientX + b.clientX) / 2 - r.left,
        cy: (a.clientY + b.clientY) / 2 - r.top,
      }
    } else if (a) {
      touch = { single: true, x: a.clientX, y: a.clientY, vx: v.tx, vy: v.ty }
    }
  }
  const onTouchMove = (e: TouchEvent) => {
    if (!touch) return
    e.preventDefault()
    const a = e.touches[0]
    const b = e.touches[1]
    if (a && b && !touch.single) {
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
      zoomAt(v, touch.cx ?? 0, touch.cy ?? 0, d / (touch.d || d))
      touch.d = d
    } else if (a && touch.single) {
      v.tx = (touch.vx ?? 0) + (a.clientX - (touch.x ?? 0))
      v.ty = (touch.vy ?? 0) + (a.clientY - (touch.y ?? 0))
      v.x = v.tx
      v.y = v.ty
    }
  }
  const onTouchEnd = () => { touch = null }

  canvas.addEventListener('wheel', onWheel, { passive: false })
  canvas.addEventListener('mousedown', onDown)
  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
  canvas.addEventListener('touchstart', onTouchStart, { passive: true })
  canvas.addEventListener('touchmove', onTouchMove, { passive: false })
  canvas.addEventListener('touchend', onTouchEnd)

  return {
    dragged: () => moved > 4,
    detach: () => {
      canvas.removeEventListener('wheel', onWheel)
      canvas.removeEventListener('mousedown', onDown)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      canvas.removeEventListener('touchstart', onTouchStart)
      canvas.removeEventListener('touchmove', onTouchMove)
      canvas.removeEventListener('touchend', onTouchEnd)
    },
  }
}
